import api from "@/api";
import { JSConn } from "@/utilities/js-conn";
import { WorkOrderLot } from "@/store/modules/workOrderLots";
import { WorkOrderNote } from "@/store/modules/workOrderNotes";
import { addDays, format } from "date-fns";
import { map as Rmap } from "ramda";
import { ordinalize } from "@/utilities/ordinalize";

const emptyWorkOrder = {
  id: null,
  name: null,
  description: null,
  start_date: null,
  submitted_date: null,
  client_field_id: null,
  commodity_id: null,
  acres: 0,
};

const fieldNameWithCutting = (
  workOrder: WorkOrderSimple | WorkOrderDetailed,
) => {
  let fieldNameWithCutting = "";

  if (workOrder.cut_number === 0) {
    fieldNameWithCutting = `${workOrder.client_field_name}`;
  } else {
    fieldNameWithCutting = `${workOrder.client_field_name}, ${ordinalize(
      workOrder.cut_number,
    )} cutting`;
  }

  return fieldNameWithCutting;
};

const apiParamsBuilder = (jsConn: JSConn) => {
  return {
    id: jsConn.formParams.id,
    work_order: {
      ...jsConn.formParams,
    },
  };
};

const itemDetails = {
  pluralName: "work_orders",
  singularName: "work_order",
};

// these are needed to be able to build the correct url in the api
const queuedWorkOrderDetails = {
  pluralName: "queued_work_orders",
  singularName: "queued_work_order",
};

const createWorkOrderDetails = {
  pluralName: "create_work_orders",
  singularName: "create_work_order",
};

interface WorkOrderSimple {
  id: string;
  name: string;
  start_date: string;
  submitted_date: string;
  scheduled_date: string;
  completed_date: null;
  archived_date: null;
  status: string;
  acres: string;
  cut_number: number;
  duration: number;
  duration_unit: string;
  commodity: string;
  commodity_color: string;
  client_id: string;
  client_name: string;
  client_field_id: string;
  client_field_name: string;
}

interface Client {
  id: string;
  name: string;
  manager: string;
  phone_number: string;
}

interface ClientField {
  id: string;
  name: string;
  description: string;
  acres: string;
  map_url: string;
  client: Client;
}

interface WorkOrderLineItem {
  id: string;
  sort_order: number;
  work_order_task_id: string;
  work_order_task_line_item_id: string;
  line_item_id: string;
  rate: string;
  item_date: string;
  quantity: string;
  description: string;
  line_item: string;
  total: string;
}

interface WorkOrderTaskLineItem {
  id: string;
  rate: string;
  quantity: string;
  item_date: string;
  sort_order: number;
  line_item_id: string;
  line_item: string;
}

interface WorkOrderTask {
  id: string;
  name: string;
  description: string;
  start_date: string;
  completed_date: null;
  status: string;
  sort_order: number;
  user_id: string;
  work_order_id: string;
  work_order_task_line_items: WorkOrderTaskLineItem[];
}

interface WorkOrderDetailed {
  id: string;
  name: string;
  start_date: string;
  submitted_date: string;
  scheduled_date: string;
  completed_date: null;
  archived_date: null;
  status: string;
  acres: string;
  cut_number: number;
  duration: number;
  duration_unit: string;
  commodity: string;
  commodity_color: string;
  client_id: string;
  client_name: string;
  client_field_id: string;
  client_field_name: string;
  description: string;
  commodity_id: string;
  client_field: ClientField;
  work_order_lots: WorkOrderLot[];
  work_order_tasks: WorkOrderTask[];
  work_order_instructions: WorkOrderNote[];
  work_order_notes: WorkOrderNote[];
  work_order_attachments: any[];
  work_order_total: string;
  line_items: WorkOrderLineItem[];
}

interface WorkOrderState {
  workOrders: WorkOrderSimple[];
  workOrder: WorkOrderDetailed | {};
}

const state: WorkOrderState = {
  workOrders: [],
  workOrder: {},
};

const mutations = {
  LOAD_WORK_ORDERS(state: WorkOrderState, payload: WorkOrderSimple[]) {
    state.workOrders = payload;
  },
  LOAD_WORK_ORDER(state: WorkOrderState, payload: WorkOrderDetailed) {
    state.workOrder = payload;
  },
};

const actions = {
  needWorkOrders(context: any, jsConn: JSConn) {
    jsConn.apiParams = jsConn.formParams;
    return api.getItems(jsConn, itemDetails).then((jsConn: JSConn) => {
      context.commit("LOAD_WORK_ORDERS", jsConn.response);
    });
  },
  needWorkOrder(context: any, jsConn: JSConn) {
    // this is getting all of the data for the work order
    // it then parses out the lots, tasks, and task line items
    // and stores them in their own module
    // as of 2019-04-08 the work order show page
    // gets the values from the respective modules
    return api.getItem(jsConn, itemDetails).then((jsConn: JSConn) => {
      const apiResponse = jsConn.response;
      context.commit("LOAD_WORK_ORDER", apiResponse);
      context.commit("LOAD_WORK_ORDER_LOTS", apiResponse.work_order_lots, {
        root: true,
      });
      context.commit("LOAD_WORK_ORDER_NOTES", apiResponse.work_order_notes, {
        root: true,
      });
      context.commit("LOAD_WORK_ORDER_TASKS", apiResponse.work_order_tasks, {
        root: true,
      });
      context.commit(
        "LOAD_WORK_ORDER_INSTRUCTIONS",
        apiResponse.work_order_instructions,
        {
          root: true,
        },
      );
      context.commit(
        "LOAD_WORK_ORDER_ATTACHMENTS",
        apiResponse.work_order_attachments,
        {
          root: true,
        },
      );
      // the line items are flattened in the api
      context.commit(
        "LOAD_WORK_ORDER_TASK_LINE_ITEMS",
        apiResponse.line_items,
        {
          root: true,
        },
      );
    });
  },
  createWorkOrderFromTemplate(context: any, jsConn: JSConn) {
    jsConn.apiParams = {
      create_work_order: {
        ...jsConn.formParams,
      },
    };
    return api
      .createItem(jsConn, createWorkOrderDetails)
      .then((jsConn: JSConn) => {
        context.commit("LOAD_WORK_ORDER", jsConn.response);
        return Promise.resolve(jsConn);
      })
      .catch((jsConn: JSConn) => {
        return Promise.reject(jsConn);
      });
  },
  createWorkOrderFromWorkOrder(context: any, jsConn: JSConn) {
    jsConn.apiParams = {
      create_work_order: {
        ...jsConn.formParams,
      },
    };
    return api
      .createItem(jsConn, createWorkOrderDetails)
      .then((jsConn: JSConn) => {
        context.commit("LOAD_WORK_ORDER", jsConn.response);
        return Promise.resolve(jsConn);
      })
      .catch((jsConn: JSConn) => {
        return Promise.reject(jsConn);
      });
  },
  createWorkOrder(context: any, jsConn: JSConn) {
    jsConn.apiParams = apiParamsBuilder(jsConn);
    return api
      .createItem(jsConn, itemDetails)
      .then((jsConn: JSConn) => {
        context.commit("LOAD_WORK_ORDER", jsConn.response);
        return Promise.resolve(jsConn);
      })
      .catch((jsConn: JSConn) => {
        return Promise.reject(jsConn);
      });
  },
  updateWorkOrder(context: any, jsConn: JSConn) {
    jsConn.apiParams = apiParamsBuilder(jsConn);

    return api
      .updateItem(jsConn, itemDetails)
      .then((jsConn: JSConn) => {
        context.commit("LOAD_WORK_ORDER", jsConn.response);
        return Promise.resolve(jsConn);
      })
      .catch((jsConn: JSConn) => {
        return Promise.reject(jsConn);
      });
  },
  queueWorkOrder(context: any, jsConn: JSConn) {
    jsConn.apiParams = jsConn.formParams;

    return api
      .updateItem(jsConn, queuedWorkOrderDetails)
      .then((jsConn: JSConn) => {
        context.commit("LOAD_WORK_ORDER", jsConn.response);
        return Promise.resolve(jsConn);
      })
      .catch((jsConn: JSConn) => {
        return Promise.reject(jsConn);
      });
  },
  deleteWorkOrder(context: any, jsConn: JSConn) {
    jsConn.apiParams = apiParamsBuilder(jsConn);

    return api
      .deleteItem(jsConn, itemDetails)
      .then((jsConn: JSConn) => {
        context.commit("LOAD_WORK_ORDER", emptyWorkOrder);
        return Promise.resolve(jsConn);
      })
      .catch((jsConn: JSConn) => {
        return Promise.reject(jsConn);
      });
  },
};

const getters = {
  workOrders: (state: WorkOrderState) => state.workOrders,

  workOrdersForTable: (state: WorkOrderState) => {
    return Rmap(workOrder => {
      const fieldWCutting = fieldNameWithCutting(workOrder);

      return {
        ...workOrder,
        field_name_with_cutting: fieldWCutting,
      };
    }, state.workOrders);
  },

  workOrdersAsEvents: (state: WorkOrderState) => {
    return Rmap(workOrder => {
      const stDate = new Date(workOrder.scheduled_date);
      const endDate = addDays(stDate, workOrder.duration);
      let htmlStyle = "";

      htmlStyle = `background-color: ${workOrder.commodity_color}`;

      const fieldWCutting = fieldNameWithCutting(workOrder);

      return {
        id: workOrder.id,
        title: `${workOrder.client_name}, ${fieldWCutting}, ${workOrder.acres} acres of ${workOrder.commodity}`,
        startDate: workOrder.scheduled_date,
        endDate: format(endDate, "yyyy-MM-dd"),
        style: htmlStyle,
      };
    }, state.workOrders);
  },

  workOrder: (state: WorkOrderState) => state.workOrder,

  workOrdersForAutocomplete: (state: WorkOrderState) => {
    return Rmap(workOrder => {
      return {
        value: workOrder.id,
        label: `${workOrder.client_name} ${workOrder.client_field_name} ${workOrder.commodity}`,
      };
    }, state.workOrders);
  },
};

const workOrdersModule = {
  state,
  mutations,
  actions,
  getters,
};

export default workOrdersModule;
